import { PhotoThumb } from './PhotoThumb.js';
import { formatDateTime } from '../format.js';
import {
  hardwarePathLabel,
  seedBtyBatteryRow,
  withAlternativeSupplyDefault,
} from '@cer-autofill/shared';
import { Link } from 'react-router-dom';
import { reindexHardwareCheckSelections } from '../lib/hardware-rows.js';
import {
  acceptOnlyReviewsKey,
  hardwareCheckSelectionCurrent,
  mergeHardwareCheckSelections,
  type AcceptOnlyReview,
} from '../lib/auto-hardware-checks.js';
import { useCallback, useEffect, useState } from 'react';
import type {
  HardwareCheckSelection,
  InspectionJob,
  StoredPhotoManifest,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  fetchJobHardwareContext,
  deleteReportPhoto,
  fetchPhotos,
  rotateReportPhoto,
  saveJobHardware,
  uploadQaPhoto,
} from '../api.js';
import { ReportHardwareEditor } from './ReportHardwareEditor.js';
import { HardwarePhotoCheck } from './HardwarePhotoCheck.js';

/** A job draft never enters the report store or starts the upload clock. */
export function JobHardwarePanel({
  job,
  onSaved,
}: {
  job: InspectionJob;
  onSaved: (job: InspectionJob) => void;
}) {
  const [context, setContext] = useState<Awaited<
    ReturnType<typeof fetchJobHardwareContext>
  > | null>(null);
  const [draft, setDraft] = useState({
    jobId: job.jobId,
    hardware: seedBtyBatteryRow(
      job.jobId,
      withAlternativeSupplyDefault(
        job.jobId,
        job.hardware ?? {
          inverters: [],
          modules: [],
          batteries: [],
          acIsolators: [],
          dcIsolators: [],
        },
      ),
    ),
  });
  const [acceptOnlyReviews, setAcceptOnlyReviews] = useState<AcceptOnlyReview[]>([]);
  const onAcceptOnlyReviews = useCallback((reviews: readonly AcceptOnlyReview[]) => {
    setAcceptOnlyReviews((current) =>
      acceptOnlyReviewsKey(current) === acceptOnlyReviewsKey(reviews) ? current : [...reviews],
    );
  }, []);
  const onSeedEmptyBattery = useCallback(() => {
    setDraft((current) => {
      const hardware = seedBtyBatteryRow(job.jobId, current.hardware);
      return hardware === current.hardware ? current : { ...current, hardware };
    });
  }, [job.jobId]);
  const [photos, setPhotos] = useState<StoredPhotoManifest[]>([]);
  const [checks, setChecks] = useState<HardwareCheckSelection[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const refreshPhotos = async () => setPhotos(await fetchPhotos(job.jobId));
  useEffect(() => {
    void fetchPhotos(job.jobId)
      .then(setPhotos)
      .catch((e) => setError(apiErrorReason(e)));
  }, [job.jobId]);
  useEffect(() => {
    void fetchJobHardwareContext(job.jobId)
      .then(setContext)
      .catch((e) => setError(apiErrorReason(e)));
  }, [job.jobId]);
  if (!context) return <p>{error || 'Loading hardware…'}</p>;
  if (context.submittedAt)
    return (
      <section className="card">
        <h3>Hardware &amp; photos</h3>
        <Link
          to={`/reports/${encodeURIComponent(job.jobId)}/${encodeURIComponent(context.submittedAt)}`}
        >
          Open report hardware
          {context.unresolvedConflictCount
            ? ` — ${context.unresolvedConflictCount} unresolved conflict(s)`
            : ''}
        </Link>
      </section>
    );
  if (!editing)
    return (
      <section className="card">
        <h3>Hardware &amp; photos</h3>
        <JobHardwareSummary job={job} photoCount={photos.length} />
        <div className="photo-grid" style={{ maxWidth: 360 }}>
          {photos
            .filter((p) => p.mimeType.startsWith('image/'))
            .slice(0, 3)
            .map((p) => (
              <PhotoThumb
                key={p.photoId}
                jobId={job.jobId}
                photoId={p.photoId}
                promptKey={p.promptKey}
                caption={p.filename}
                mimeType={p.mimeType}
                version={p.uploadedAt}
                onOpen={() => {
                  setAcceptOnlyReviews([]);
                  setEditing(true);
                }}
              />
            ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setAcceptOnlyReviews([]);
            setEditing(true);
          }}
        >
          Edit hardware &amp; photos
        </button>
      </section>
    );
  return (
    <section className="card">
      <h3>Hardware &amp; photos</h3>
      <p>Save hardware before the inspection. Photos save immediately.</p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <HardwarePhotoCheck
        // Stable key: remounting on every job.updatedAt (e.g. cover save) reset
        // auto-check startedChecks and re-fired POSTs against a new cache id.
        key={job.jobId}
        report={{ jobId: job.jobId, submittedAt: `job:${job.updatedAt}`, status: 'pending' }}
        hardware={draft.hardware}
        photos={photos}
        disabled={busy}
        onSeedEmptyBattery={onSeedEmptyBattery}
        onAcceptOnlyReviews={onAcceptOnlyReviews}
        onApply={(hardware, selections) => {
          setDraft({ ...draft, hardware });
          setChecks((current) => mergeHardwareCheckSelections(current, selections));
        }}
      />
      <ReportHardwareEditor
        draft={draft}
        batteryInstallationType={job.batteryInstallationType ?? null}
        suggestionReviewCount={acceptOnlyReviews.length}
        suggestionReviewFields={acceptOnlyReviews.map((item) =>
          item.surface === 'switchboard'
            ? `switchboard:${item.field}`
            : `${item.kind}:${item.field}`,
        )}
        onChange={setDraft}
        photos={photos}
        onRowRemoved={(kind, index) =>
          setChecks((current) => reindexHardwareCheckSelections(current, kind, index))
        }
        onUploadPhoto={async (promptKey, file) => {
          await uploadQaPhoto(job.jobId, { photoId: crypto.randomUUID(), promptKey, file });
          await refreshPhotos();
        }}
        onDeletePhoto={async (photoId) => {
          if (!window.confirm('Delete this photo? This cannot be undone.')) return;
          await deleteReportPhoto(job.jobId, photoId);
          await refreshPhotos();
        }}
        onRotatePhoto={async (photoId, degrees) => {
          await rotateReportPhoto(job.jobId, photoId, degrees);
          await refreshPhotos();
        }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError('');
          const hardware = seedBtyBatteryRow(job.jobId, draft.hardware);
          void saveJobHardware(
            job,
            hardware,
            checks.filter((c) => hardwareCheckSelectionCurrent(hardware, c)),
          )
            .then((saved) => {
              onSaved(saved);
              setChecks([]);
              setEditing(false);
            })
            .catch((e) => setError(apiErrorReason(e)))
            .finally(() => setBusy(false));
        }}
      >
        Save hardware
      </button>
    </section>
  );
}

function batterySystemTypeSummary(systemType: string): string {
  if (systemType === 'integrated') return 'Section 4';
  if (systemType === 'pre-assembled') return 'Section 5';
  if (systemType === 'other') return 'Section 6';
  return systemType;
}

function batteryInstallLocationSummary(location: string): string {
  if (location === 'internal') return 'Internal';
  if (location === 'external') return 'External';
  return location;
}

export function JobHardwareSummary({
  job,
  photoCount,
}: {
  job: InspectionJob;
  photoCount: number;
}) {
  const rows = Object.entries(job.hardware ?? {}).flatMap(([kind, value]) =>
    Array.isArray(value)
      ? value
          .map((entry, index) => ({ entry, index }))
          .filter(
            ({ entry }) =>
              typeof entry === 'object' && entry !== null && Object.values(entry).some(Boolean),
          )
          .map(({ entry, index }) => {
            const row = entry as Record<string, unknown>;
            return (
              <p key={`${kind}-${index}`}>
                {hardwarePathLabel(`${kind}[${index}].make`).split(' · ')[0]}:{' '}
                {[row.make, row.model].filter(Boolean).join(' ') || 'Details entered'}
                {typeof row.serial === 'string' && row.serial ? ` · S/N ${row.serial}` : ''}
                {kind === 'batteries' && typeof row.systemType === 'string'
                  ? ` · ${batterySystemTypeSummary(row.systemType)}`
                  : ''}
                {kind === 'batteries' && typeof row.installLocation === 'string'
                  ? ` · ${batteryInstallLocationSummary(row.installLocation)}`
                  : ''}
              </p>
            );
          })
      : [],
  );
  const hardware = job.hardware;
  const switchboard = [
    hardware?.circuitBreakerSizeMainSwitchGrid
      ? `Main switch (grid) breaker: ${hardware.circuitBreakerSizeMainSwitchGrid}`
      : '',
    hardware?.circuitBreakerSizeMainSwitchInverter
      ? `Main switch inverter/alternative breaker: ${hardware.circuitBreakerSizeMainSwitchInverter}`
      : '',
    hardware?.alternativeSupplyFromBessInverter !== undefined
      ? `Alternative supply from the BESS/inverter: ${hardware.alternativeSupplyFromBessInverter ? 'Yes' : 'No'}`
      : '',
    hardware?.switchboardLabellingRequirementsMet !== undefined
      ? `Switchboard labelling requirements met: ${hardware.switchboardLabellingRequirementsMet ? 'Yes' : 'No'}`
      : '',
  ].filter(Boolean);
  const otherDetails = Object.entries(hardware ?? {}).some(([key, value]) => {
    if (Array.isArray(value) || value === undefined) return false;
    return ![
      'circuitBreakerSizeMainSwitchGrid',
      'circuitBreakerSizeMainSwitchInverter',
      'alternativeSupplyFromBessInverter',
      'switchboardLabellingRequirementsMet',
    ].includes(key);
  });
  return (
    <div>
      {switchboard.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {rows}
      {!rows.length && !switchboard.length && otherDetails && <p>System details entered</p>}
      {!rows.length && !switchboard.length && !otherDetails && <p>Nothing entered yet</p>}
      <p>{photoCount} photo(s)</p>
      {job.hardwareMeta && (
        <p className="muted">
          Last edited by {job.hardwareMeta.editedBy} at {formatDateTime(job.hardwareMeta.editedAt)}
        </p>
      )}
    </div>
  );
}
